import {
  useEffect, useMemo, useRef, useState,
} from 'react'
import { useDispatch } from 'react-redux'
import { useSelector } from 'src/utils/store'
import { useForm } from 'react-hook-form'
import { actions } from '../../ShedManagementState'
import { FormAddLocationData } from '../../ShedManagementTypes'
import { Box } from '@mui/material'
import ErrorMessage from 'src/components/ErrorMessage'
import CsvValidationErrorTable from '../CsvValidationErrorTable'
import * as S from './FormAddLocation.styles'
import { allFulfillmentCenterTypeFilters } from 'src/features/fulfillmentCenterFilters'
import { selectAllFulfillmentCenters } from 'src/features/selector'
import { actions as appActions } from 'src/features/state'

interface FormAddLocationProps {
  onSubmit?: (data: FormAddLocationData) => void
}

const validCSVTypes = [
  'text/csv',
  'application/csv',
]

const csvErrorMessage = 'É necessário definir um arquivo CSV (.csv) antes de proceder'

const FormAddLocation: React.FC<FormAddLocationProps> = ({ onSubmit }) => {
  const {
    handleSubmit, setValue,
  } = useForm<FormAddLocationData>()
  const dispatch = useDispatch()
  const { createFiltersData, csvValidationErrors } = useSelector((state) => state.shedManagement)

  const [selectedFulfillmentCenter, setSelectedFulfillmentCenter] = useState<number | ''>('')
  const [selectedAddressType, setSelectedAddressType] = useState<number | ''>('')
  const [selectedAddressHeight, setSelectedAddressHeight] = useState<number | ''>('')

  const [fcError, setFcError] = useState<string>('')
  const [addressTypeError, setAddressTypeError] = useState<string>('')
  const [addressHeightError, setAddressHeightError] = useState<string>('')

  const [csvFile, setCsvFile] = useState<File | undefined>()
  const [showCsvError, setShowCsvError] = useState<boolean>(false)
  const inputFileRef = useRef<HTMLInputElement | null>(null)

  const fulfillmentCenters = useSelector(selectAllFulfillmentCenters)

  const isValidCSVFile = (testFile: File) => validCSVTypes.includes(testFile.type) || testFile.name.toLowerCase().endsWith('.csv')

  const clearCsvFile = () => {
    setCsvFile(undefined)
    if (inputFileRef.current) {
      inputFileRef.current.value = ''
    }
  }

  const handleCsvFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCsvFile(undefined)
    const fileInput = event?.target?.files?.[0] as File

    if (!fileInput) {
      setShowCsvError(true)
      clearCsvFile()
      return
    }

    if (!isValidCSVFile(fileInput)) {
      setShowCsvError(true)
      clearCsvFile()
      return
    }

    setCsvFile(fileInput)
    setValue('csvFile', fileInput)
    setShowCsvError(false)
  }

  const handleFormSubmit = () => {
    setFcError('')
    setAddressTypeError('')
    setAddressHeightError('')

    let hasErrors = false

    if (selectedFulfillmentCenter === '' || selectedFulfillmentCenter === 0) {
      setFcError('Campo obrigatório')
      hasErrors = true
    }
    if (selectedAddressType === '' || selectedAddressType === 0) {
      setAddressTypeError('Campo obrigatório')
      hasErrors = true
    }
    if (selectedAddressHeight === '' || selectedAddressHeight === 0) {
      setAddressHeightError('Campo obrigatório')
      hasErrors = true
    }

    if (!csvFile) {
      setShowCsvError(true)
      hasErrors = true
    }

    if (hasErrors) {
      return
    }

    const completeFormData: FormAddLocationData = {
      fulfillmentCenterId: Number(selectedFulfillmentCenter),
      addressType: Number(selectedAddressType),
      addressHeight: Number(selectedAddressHeight),
      csvFile: csvFile!,
    }

    if (onSubmit) {
      onSubmit(completeFormData)
    } else {
      const payload = {
        id_fulfillment_center: Number(selectedFulfillmentCenter),
        id_location_type: Number(selectedAddressType),
        id_location_height_type: Number(selectedAddressHeight),
        planilha: csvFile!,
      }
      dispatch(actions.createLocationsByCsv(payload))
    }
  }

  const fulfillmentCenterOptions = useMemo(() => {
    if (!fulfillmentCenters) {
      return []
    }

    return fulfillmentCenters.map((fulfillmentCenter) => ({
      id: fulfillmentCenter.id,
      description: fulfillmentCenter.name,
    }))
  }, [fulfillmentCenters])

  const getLocationTypeDescription = (name: string): string => {
    const translations: Record<string, string> = {
      NORMAL: 'Normal',
      OPERATIONAL: 'Operacional (Não calculado no estoque)',
      PK: 'PK',
      PKP: 'PKP',
      TRANSFER_CAGE: 'Gaiola de Transferência',
      MOVEMENT: 'Movimentação',
    }
    return translations[name] || name
  }

  const addressTypeOptions = useMemo(() => {
    if (!createFiltersData?.locationTypes) {
      return []
    }

    return createFiltersData.locationTypes.map((locationType) => ({
      id: locationType.id,
      description: getLocationTypeDescription(locationType.name),
    }))
  }, [createFiltersData])

  const addressHeightOptions = useMemo(() => {
    if (!createFiltersData?.locationHeightTypes) {
      return []
    }

    return createFiltersData.locationHeightTypes.map((heightType) => ({
      id: heightType.id,
      description: heightType.name,
    }))
  }, [createFiltersData])

  useEffect(() => {
    dispatch(appActions.getAvailableFulfillmentCenters(allFulfillmentCenterTypeFilters))
  }, [dispatch])

  useEffect(() => {
    dispatch(actions.getCreateFilters)
  }, [dispatch])

  return (
    <Box
      component='form'
      onSubmit={handleSubmit(handleFormSubmit)}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        width: '100%',
        margin: '1.3rem 0 0 0',
        gap: '1rem',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '1rem',
          width: '100%',
        }}
      >
        <S.CustomSelect
          name='fulfillmentCenterId'
          value={selectedFulfillmentCenter}
          onChange={(e) => {
            const value = e.target.value as number | ''
            setSelectedFulfillmentCenter(value)
            setValue('fulfillmentCenterId', value === '' ? 0 : Number(value))
            if (value === '' || value === 0) {
              setFcError('Campo obrigatório')
            } else {
              setFcError('')
            }
          }}
          displayEmpty
          inputProps={{
            'data-testid': 'fc-search-location',
          }}
          dataTestId='fulfillment-center-select'
          autoComplete='off'
          id='fulfillmentCenterId'
          label='Selecione um FC'
          variant='outlined'
          options={fulfillmentCenterOptions}
          error={!!fcError}
          helperText={fcError}
          optionNone={false}
          type='text'
          fullWidth={false}
          autoFocus
          maxHeight='600px'
        />
        <S.CustomSelect
          name='addressType'
          value={selectedAddressType}
          onChange={(e) => {
            const value = e.target.value as number | ''
            setSelectedAddressType(value)
            setValue('addressType', value === '' ? 0 : Number(value))
            if (value === '' || value === 0) {
              setAddressTypeError('Campo obrigatório')
            } else {
              setAddressTypeError('')
            }
          }}
          displayEmpty
          inputProps={{
            'data-testid': 'address-type-select',
          }}
          dataTestId='address-type-select'
          autoComplete='off'
          id='addressType'
          label='Tipo de Endereço'
          variant='outlined'
          options={addressTypeOptions}
          error={!!addressTypeError}
          helperText={addressTypeError}
          optionNone={false}
          type='text'
          fullWidth={false}
          maxHeight='600px'
        />
        <S.CustomSelect
          name='addressHeight'
          value={selectedAddressHeight}
          onChange={(e) => {
            const value = e.target.value as number | ''
            setSelectedAddressHeight(value)
            setValue('addressHeight', value === '' ? 0 : Number(value))
            if (value === '' || value === 0) {
              setAddressHeightError('Campo obrigatório')
            } else {
              setAddressHeightError('')
            }
          }}
          displayEmpty
          inputProps={{
            'data-testid': 'address-height-select',
          }}
          dataTestId='address-height-select'
          autoComplete='off'
          id='addressHeight'
          label='Altura do Endereço'
          variant='outlined'
          options={addressHeightOptions}
          error={!!addressHeightError}
          helperText={addressHeightError}
          optionNone={false}
          type='text'
          fullWidth={false}
          maxHeight='600px'
        />

        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '0.5rem',
          minWidth: '300px',
        }}
        >
          <Box
            component='label'
            htmlFor='csvFile'
            sx={{
              fontSize: '0.875rem',
              fontWeight: 500,
              color: '#333',
            }}
          >
            Arquivo CSV *
          </Box>
          <input
            onChange={handleCsvFileChange}
            onClick={() => {
              if (inputFileRef.current) {
                inputFileRef.current.value = ''
                setCsvFile(undefined)
              }
            }}
            type='file'
            name='csvFile'
            id='csvFile'
            accept='.csv,text/csv,application/csv'
            ref={inputFileRef}
            style={{
              padding: '10px',
              border: `1px solid ${showCsvError ? '#d32f2f' : '#ccc'}`,
              borderRadius: '4px',
              fontSize: '0.875rem',
              backgroundColor: '#fff',
            }}
            data-testid='csv-file-input'
          />
          {showCsvError && <ErrorMessage message={csvErrorMessage} />}
          {csvFile && (
            <span style={{
              fontSize: '0.75rem',
              color: '#666',
            }}
            >
              Arquivo selecionado: {csvFile.name}
            </span>
          )}
        </Box>
      </Box>

      <Box sx={{ alignSelf: 'center' }}>
        <S.CustomButton
          title='Cadastrar'
          type='submit'
          bgColor='blue'
          hoverColor='darkBlueHover'
          letterColor='white'
          data-testid='search-location-button'
        />
      </Box>

      <CsvValidationErrorTable errors={csvValidationErrors} />
    </Box>
  )
}

export default FormAddLocation
